import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';

type Tone = 'neutral' | 'info' | 'warning' | 'danger' | 'success';

const toneClasses: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  info: 'bg-primary/10 text-primary',
  warning: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  danger: 'bg-destructive/10 text-destructive',
  success: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
};

type Props = {
  label: string;
  value: React.ReactNode;
  icon: LucideIcon;
  tone?: Tone;
  hint?: string;
  to?: string;
};

export default function StatTile({ label, value, icon: Icon, tone = 'info', hint, to }: Props) {
  const interactive = Boolean(to);
  const card = (
    <Card
      className={cn(
        'py-4 h-full',
        interactive && 'cursor-pointer transition-colors hover:border-primary/40 hover:bg-accent/40',
      )}
    >
      <CardContent className="flex flex-col gap-3 px-4">
        <div className={cn('flex size-9 items-center justify-center rounded-lg', toneClasses[tone])}>
          <Icon className="size-[18px]" />
        </div>
        <div className="min-w-0">
          <div className="text-3xl font-semibold tabular-nums leading-none text-foreground">
            {value}
          </div>
          <div className="mt-1.5 text-[13px] font-medium text-muted-foreground">{label}</div>
          {hint && <div className="mt-0.5 text-[11px] text-muted-foreground/70">{hint}</div>}
        </div>
      </CardContent>
    </Card>
  );

  if (to) {
    return (
      <Link to={to} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl">
        {card}
      </Link>
    );
  }
  return card;
}
